const runtimeConfig = typeof window !== 'undefined' ? window.__APP_CONFIG__ || {} : {};
const API_ORIGIN = String(runtimeConfig.API_URL || import.meta.env.VITE_API_URL || '').replace(/\/$/, '');

export const API_BASE = API_ORIGIN ? `${API_ORIGIN}/api` : '/api';
export const ROOT_DOMAIN = String(runtimeConfig.ROOT_DOMAIN || import.meta.env.VITE_ROOT_DOMAIN || 'miskipop.com');
export const SITE_URL = String(runtimeConfig.SITE_URL || import.meta.env.VITE_SITE_URL || `https://${ROOT_DOMAIN}`).replace(/\/$/, '');

function isCustomerSessionPath(path) {
  return Boolean(path && (path.startsWith('/customer/') || path.startsWith('/checkout')));
}

export function getToken(path) {
  if (isCustomerSessionPath(path)) {
    return localStorage.getItem('customer_token');
  }

  if (path && path.startsWith('/cart')) {
    return null;
  }

  return localStorage.getItem('admin_token');
}

export function clearExpiredSession(path) {
  if (isCustomerSessionPath(path)) {
    localStorage.removeItem('customer_token');
    localStorage.removeItem('customer_user');
    return;
  }

  localStorage.removeItem('admin_token');
  localStorage.removeItem('admin_user');
  if (window.location.pathname.startsWith('/admin')) {
    window.location.href = '/?login=true';
  }
}

export async function apiFetch(path, options = {}) {
  const token = getToken(path);
  const hasToken = Boolean(token);
  const headers = { 'Content-Type': 'application/json', ...options.headers };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const safePaths = new Set([
    '/auth/login',
    '/customer/auth/login',
    '/auth/refresh',
    '/customer/auth/refresh',
    '/auth/me',
    '/customer/auth/me'
  ]);

  const performRequest = () => fetch(`${API_BASE}${path}`, { ...options, headers, credentials: 'include' });

  let res = await performRequest();

  const shouldAttemptRefresh =
    hasToken &&
    res.status === 401 &&
    !safePaths.has(path);

  if (shouldAttemptRefresh) {
    const isCustomer = isCustomerSessionPath(path);
    const refreshPath = isCustomer ? '/customer/auth/refresh' : '/auth/refresh';

    try {
      const refreshRes = await fetch(`${API_BASE}${refreshPath}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: '{}'
      });
      if (refreshRes.ok) {
        const session = await refreshRes.json();
        const newToken = session.tokens?.accessToken;
        if (newToken) {
          localStorage.setItem(isCustomer ? 'customer_token' : 'admin_token', newToken);
          headers.Authorization = `Bearer ${newToken}`;
          res = await performRequest();
        }
      }
    } catch {
      // Refresh failed, continue to error handling.
    }
  }

  if (res.status === 401 && !safePaths.has(path)) {
    if (hasToken) {
      clearExpiredSession(path);
    }
    throw new Error('Sesión expirada');
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const msg = err.message || (err.errors ? err.errors.map((e) => e.message).join(', ') : null) || `Error ${res.status}`;
    const error = new Error(msg);
    error.status = res.status;
    error.data = err;
    throw error;
  }

  if (res.status === 204) return null;

  return res.json();
}

export async function fetchAuthorizedBlob(path, { fileNameFallback, tokenPath } = {}) {
  const token = getToken(tokenPath);
  const res = await fetch(`${API_BASE}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: 'include',
  });

  if (res.status === 401) {
    clearExpiredSession(tokenPath);
    throw new Error('Sesión expirada');
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `Error ${res.status}`);
  }

  const disposition = res.headers.get('Content-Disposition') || '';
  const fileName = disposition.match(/filename="?([^";]+)"?/)?.[1] || fileNameFallback || 'download.bin';
  return { blob: await res.blob(), fileName };
}

export async function uploadWithToken(path, file, folder, tokenKey = 'admin_token', fields = {}) {
  const token = localStorage.getItem(tokenKey);
  const form = new FormData();
  form.append('file', file);
  form.append('folder', folder);
  Object.entries(fields).forEach(([key, value]) => {
    if (value !== undefined && value !== null) form.append(key, String(value));
  });

  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });

  if (res.status === 401) {
    clearExpiredSession(tokenKey === 'customer_token' ? '/customer/upload' : '/uploads/upload');
    throw new Error('Sesión expirada');
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `Error ${res.status}`);
  }

  return res.json();
}
